<!-- @format -->
<template>
    <div class="collateral-build-widget">
        <div class="widget-header">
            <h3>Create Collateral</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <loading-svg v-if="loading" />
        <div v-else class="content-wrapper">
            <div class="create-step">
                <app-input
                    v-model="collateralBuild.name"
                    label="What's the name of your collateral build?"
                    description="Differentiate your collateral builds on your dashboard."
                    :is-valid="!submitted || $v.collateralBuild.name.required"
                    error="Name is required"
                />
                <div class="page-size-group bordered-group">
                    <span class="label">Choose your page size</span>
                    <ul class="sizes">
                        <li
                            v-for="pageSize in pageSizes"
                            :key="pageSize.key"
                            class="size"
                            :class="[
                                'size-' + pageSize.key,
                                { selected: collateralBuild.pageSize === pageSize.key },
                            ]"
                            @click="selectSize(pageSize)"
                        >
                            <div class="preview">
                                <div class="name">{{ pageSize.name }}</div>
                                <div class="dimensions">{{ pageSize.dimensions }}</div>
                            </div>
                            <div class="radio-btn-container">
                                <div
                                    class="radio-btn"
                                    :class="{
                                        selected: collateralBuild.pageSize === pageSize.key,
                                    }"
                                >
                                    <div class="selected-dot"></div>
                                </div>
                            </div>
                        </li>
                    </ul>
                    <div class="custom-measurements">
                        <div v-if="collateralBuild.pageSize === 'Custom'">
                            <ul class="measurements-input">
                                <li class="width">
                                    <label for="widthInput">Width:</label>
                                    <app-input
                                        type="number"
                                        v-model="customWidth"
                                        placeholder="Enter width"
                                    />
                                </li>
                                <li class="height">
                                    <label for="heightInput">Height:</label>
                                    <app-input
                                        type="number"
                                        v-model="customHeight"
                                        placeholder="Enter height"
                                    />
                                </li>
                                <li class="measurement">
                                    <v-select
                                        placeholder="PX"
                                        v-model="customUnits"
                                        :items="measurementUnits"
                                        @input="applySize"
                                    ></v-select>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="categories-row">
                    <global-category-dropdown
                        :account-id="accountId"
                        :selected="collateralBuild.categories"
                        show-selected-up
                        @input="addListCategories($event)"
                        @remove="deleteCategory"
                    />
                </div>
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget">Cancel</app-button>
            </div>
            <div class="action-container">
                <app-button
                    v-if="collateralBuild.id"
                    :loading="isSaving"
                    :disabled="loading"
                    @click="saveAndClose"
                >
                    {{ isSaving ? 'Saving' : 'Save and Close' }}
                </app-button>
                <app-button
                    v-if="!collateralBuild.id"
                    :loading="isSaving"
                    :disabled="loading"
                    @click="next"
                >
                    {{ isSaving ? 'Saving' : 'Next Step' }}
                </app-button>
                <app-button v-else :loading="isSaving" :disabled="loading" @click="next">
                    {{ isSaving ? 'Saving' : 'Save & View Collateral ' }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const AppInput = () => import('@/components/common/app-input');
const GlobalCategoryDropdown = () => import('@/components/common/global-category-dropdown');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import { required } from 'vuelidate/lib/validators';
import collateralBuilderMixin from '@/mixins/collateral-builder-mixin';

export default {
    name: 'collateral-build-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'app-input': AppInput,
        'global-category-dropdown': GlobalCategoryDropdown,
        'loading-svg': LoadingSvg,
    },

    mixins: [collateralBuilderMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    validations: {
        collateralBuild: {
            name: {
                required,
            },
        },
    },

    data() {
        return {
            submitted: false,

            isSaving: false,

            loading: true,

            collateralBuild: {
                id: this.widget.collateralBuildId,
                name: null,
                pageSize: null,
                previewImageUrl: null,
                clientId: this.widget.clientId,
                agencyId: this.widget.agencyId,
                categories: [],
                configuration: null,
            },

            measurementUnits: ['PX', 'INCHES', 'MM', 'CM'],

            pageSizes: this.$helpers.getPageSizes(),
            customWidth: 1500,
            customHeight: 2100,
            customUnits: 'PX',
        };
    },

    computed: {
        accountId() {
            return this.widget.accountId;
        },

        isFromStudio() {
            return this.widget.options ? this.widget.options.isFromStudio : false;
        },
    },

    created() {
        if (this.collateralBuild.id) {
            this.getCollateralBuild();
        } else {
            this.collateralBuild.pageSize = this.pageSizes[1].key;
            this.loading = false;
        }
    },

    methods: {
        selectSize(pageSize) {
            this.collateralBuild.pageSize = pageSize.key;
        },

        next() {
            this.submitted = true;
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }
            if (!this.collateralBuild.id) {
                this.post().then(() => {
                    this.closeWidget();
                    this.$router.push(`/CollateralBuilds/${this.collateralBuild.id}/Design`);
                });
            } else {
                this.patch().then(() => {
                    const dest =
                        `/CollateralBuilds/${this.collateralBuild.id}/Design` +
                        `${this.isFromStudio ? '?fromStudio=true' : ''}`;

                    this.closeWidget();
                    this.$router.push(dest);
                });
            }
        },

        saveAndClose() {
            this.submitted = true;
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            if (!this.collateralBuild.id) {
                this.post().then(() => {
                    this.closeWidget();
                });
            } else {
                this.patch().then(() => {
                    this.closeWidget();
                });
            }
        },

        updateCustomConfiguration() {
            if (this.collateralBuild.pageSize === 'Custom') {
                if (!this.collateralBuild.configuration) {
                    this.collateralBuild.configuration = this.getDefaultCollateralBuildConfiguration(
                        null
                    );
                }
                this.collateralBuild.configuration.customSettings = {
                    ...(this.collateralBuild.configuration.customSettings || {}),
                    customWidth: this.customWidth,
                    customHeight: this.customHeight,
                    customUnits: this.customUnits,
                };
            }
            return this.collateralBuild.configuration;
        },

        getJsonOrNull(object) {
            return object ? JSON.stringify(object) : null;
        },

        getPostRequest() {
            const configurationJson = this.getJsonOrNull(this.updateCustomConfiguration());
            return {
                name: this.collateralBuild.name,
                pageSize: this.collateralBuild.pageSize,
                clientId: this.widget.clientId,
                agencyId: this.widget.agencyId,
                categories: this.collateralBuild.categories,
                hasBranding: false,
                configuration: configurationJson,
            };
        },

        getUpdateRequest() {
            const configurationJson = this.getJsonOrNull(this.updateCustomConfiguration());
            return [
                {
                    name: 'Name',
                    value: this.collateralBuild.name,
                },
                {
                    name: 'PageSize',
                    value: this.collateralBuild.pageSize,
                },
                {
                    name: 'Categories',
                    value: this.collateralBuild.categories,
                },
                {
                    name: 'Configuration',
                    value: configurationJson,
                },
            ];
        },

        post() {
            return new Promise((resolve, reject) => {
                this.isSaving = true;

                this.$api
                    .post('/api/CollateralBuilds', this.getPostRequest())
                    .then((response) => {
                        this.collateralBuild.id = response.data.Id;
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while saving your changes.',
                            { error },
                        );
                        reject();
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
            });
        },

        patch() {
            return new Promise((resolve, reject) => {
                this.isSaving = true;
                this.$api
                    .patch(
                        `/api/CollateralBuilds/${this.collateralBuild.id}`,
                        this.getUpdateRequest()
                    )
                    .then(() => {
                        EventBus.$emit('collateral-build:update');
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while saving your changes.',
                            { error },
                        );
                        reject();
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
            });
        },

        getCollateralBuild() {
            this.loading = true;
            var filterParam = `$filter=Id eq ${this.collateralBuild.id}`;
            var selectParam =
                '$select=Name,PageSize,PreviewImageUrl,ClientId,AgencyId,Configuration';
            var expandParam = '$expand=Categories';

            this.$api
                .get(`/service/CollateralBuilds?${filterParam}&${selectParam}&${expandParam}`)
                .then((response) => {
                    this.collateralBuild.clientId = response.data.value[0].ClientId;
                    this.collateralBuild.agencyId = response.data.value[0].AgencyId;
                    this.collateralBuild.name = response.data.value[0].Name;
                    this.collateralBuild.pageSize = response.data.value[0].PageSize;
                    this.collateralBuild.previewImageUrl = response.data.value[0].PreviewImageUrl;
                    this.collateralBuild.categories = response.data.value[0].Categories.map(
                        ({ Name }) => Name
                    );
                    this.collateralBuild.configuration = response.data.value[0].Configuration
                        ? JSON.parse(response.data.value[0].Configuration)
                        : null;
                    if (
                        this.collateralBuild.configuration &&
                        this.collateralBuild.configuration.customSettings
                    ) {
                        const customSettings = this.collateralBuild.configuration.customSettings;
                        if (customSettings.customWidth) {
                            this.customWidth = customSettings.customWidth;
                        }
                        if (customSettings.customHeight) {
                            this.customHeight = customSettings.customHeight;
                        }
                        if (customSettings.customUnits) {
                            this.customUnits = customSettings.customUnits;
                        }
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(error, { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        addListCategories(item) {
            const exist = this.collateralBuild.categories.some((element) => element === item.name);

            if (!exist) {
                this.collateralBuild.categories.push(item.name);
            }
        },

        deleteCategory(item) {
            this.collateralBuild.categories.splice(
                this.collateralBuild.categories.indexOf(item),
                1
            );
        },

        applySize() {
            if (isNaN(this.customWidth) || isNaN(this.customHeight)) {
                this.$helpers.showFailNotification(
                    'Please enter valid numeric values for width and height.',
                );
            }
        },
    },
};
</script>

<style>
.collateral-build-widget .sizes,
.collateral-build-widget .measurements-input {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    flex-wrap: wrap;
    column-gap: 16px;
    margin-bottom: 32px;
}

.collateral-build-widget .size {
    text-align: center;
    cursor: pointer;
}

.collateral-build-widget .size .radio-btn-container,
.collateral-build-widget .size .radio-btn-container .radio-btn {
    margin: 0;
}

.collateral-build-widget .size .preview {
    box-shadow: 0px 3px 12px #8274e833;
    border: 2px solid #e9e9ef;
    border-radius: 11px;
    margin-bottom: 30px;
    color: #877d88;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.collateral-build-widget .size.selected .preview {
    color: #18aeff;
    border: 2px solid #18aeff;
}

.collateral-build-widget .size .preview .name {
    font-size: 18px;
    font-weight: 500;
}

.collateral-build-widget .size-Paper4x6 .preview .name,
.collateral-build-widget .size-Paper5x7 .preview .name {
    font-size: 14px;
    font-weight: 500;
}

.collateral-build-widget .size-Custom .preview {
    width: 115px;
    height: 164px;
}

.collateral-build-widget .size-A4 .preview {
    width: 115px;
    height: 164px;
}

.collateral-build-widget .size-Letter .preview {
    width: 108px;
    height: 151px;
}

.collateral-build-widget .size-Paper5x7 .preview {
    width: 77px;
    height: 106px;
}

.collateral-build-widget .size-Paper4x6 .preview {
    width: 60px;
    height: 84px;
}

.collateral-build-widget .page-size-group > .label {
    left: calc(50% - 125px);
}

.collateral-build-widget .bordered-group {
    border-radius: 20px;
    padding: 40px 30px 30px 30px;
    border: 2px solid #e9e9ef;
    position: relative;
    margin-top: 50px;
}

.collateral-build-widget .bordered-group > .label {
    position: absolute;
    top: -12px;
    font-size: 18px;
    font-weight: 600;
    color: #382153;
    background: #ffffff;
    display: block;
    padding: 0 30px;
    margin: 0;
}

.collateral-build-widget .action-container {
    display: flex;
}

.collateral-build-widget .action-container button {
    margin-left: 20px;
}

.collateral-build-widget .categories-row {
    margin-top: 30px;
}

.collateral-build-widget .categories .category {
    background: #877d88;
    color: #ffffff;
    font-size: 11px;
    padding: 0px 10px;
    border-radius: 15px;
    margin: 0 7px 9px 7px;
    display: flex;
    align-items: center;
}

.collateral-build-widget .categories .category svg {
    margin-left: 12px;
    width: 7px;
    cursor: pointer;
}

.collateral-build-widget .categories .category svg path {
    fill: #ffffff;
}

.collateral-build-widget .categories {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -7px;
}

.collateral-build-widget .collateral-category-dropdown {
    margin-bottom: 15px;
}

.collateral-build-widget .loading-screen {
    display: flex;
    align-items: center;
    justify-content: space-around;
    width: 100%;
}

.collateral-build-widget .transparent-btn {
    background: transparent;
    color: #877d88;
}

.collateral-build-widget .transparent-btn:hover {
    background: #e1e0e5;
}

.collateral-build-widget .measurements-input {
    margin-top: 16px;
    column-gap: 16px;
}

.collateral-build-widget .v-select {
    background: #fafafa;
    font: normal 16px/1em 'Roboto';
    width: 100%;
    border: 1px solid #cac6cb;
    padding: 10px 16px;
    border-radius: 40px;
    color: #382153;
    outline: none;
}

.collateral-build-widget .v-icon {
    color: #ee256b !important;
    border-color: #ee256b transparent transparent;
    border-width: 7px 7px 0;
    margin-top: -0.5em;
}

.collateral-build-widget .v-text-field__details,
.collateral-build-widget .v-messages {
    min-height: 0;
}

.collateral-build-widget .v-text-field > .v-input__control > .v-input__slot:before,
.collateral-build-widget .v-text-field > .v-input__control > .v-input__slot:after {
    display: none !important;
}

.collateral-build-widget .v-input__slot {
    margin-bottom: -12px;
}

.collateral-build-widget .v-select.v-text-field:not(.v-text-field--single-line) input,
.collateral-build-widget .v-select__selections .v-select__selection {
    margin-top: -9px;
    font: normal 16px/1em 'Roboto';
}

.collateral-build-widget .width,
.collateral-build-widget .height,
.collateral-build-widget .measurement {
    max-width: 140px;
}

.collateral-build-widget .size-Custom .preview {
    padding: 0 10px;
}
</style>
